<template>
  <div class="matrix-widget">
    <div class="widget-header">
      <h3 v-if="title">{{ title }}</h3>
      <button class="export-btn" @click="exportToExcel">Export XLSX</button>
    </div>
    <div class="table-scroll">
      <table class="matrix-table">
        <thead>
          <tr>
            <th class="row-label" rowspan="2">{{ rowLabel }}</th>
            <th v-for="col in columns" :key="col" colspan="2" class="col-group">{{ col }}</th>
            <th colspan="2" class="col-group">Total</th>
          </tr>
          <tr>
            <template v-for="col in columns" :key="col + '-sub'">
              <th class="val">{{ valueLabel }}</th>
              <th class="pct">%</th>
            </template>
            <th class="val">{{ valueLabel }}</th>
            <th class="pct">%</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rows" :key="row.label">
            <td class="row-label">{{ row.label }}</td>
            <template v-for="col in columns" :key="row.label + '-' + col">
              <td class="val" :class="{ 'metric-cell': metrics.length && row.cells[col] }"
                  @mouseenter="onCellEnter(row.label, col, $event)" @mousemove="onCellMove" @mouseleave="onCellLeave">
                {{ row.cells[col] ? formatNum(row.cells[col]) : '' }}
              </td>
              <td class="pct">{{ row.cells[col] ? formatPct(row.cells[col] / row.total) : '' }}</td>
            </template>
            <td class="val"><strong>{{ formatNum(row.total) }}</strong></td>
            <td class="pct"><strong>100%</strong></td>
          </tr>
        </tbody>
        <tfoot>
          <tr class="total-row">
            <td class="row-label"><strong>Total</strong></td>
            <template v-for="col in columns" :key="'tot-' + col">
              <td class="val"><strong>{{ formatNum(colTotals[col]) }}</strong></td>
              <td class="pct"><strong>{{ formatPct((colTotals[col] || 0) / (grandTotal || 1)) }}</strong></td>
            </template>
            <td class="val"><strong>{{ formatNum(grandTotal) }}</strong></td>
            <td class="pct"><strong>100%</strong></td>
          </tr>
        </tfoot>
      </table>
    </div>

    <!-- Tooltip d'agregades de la cel·la fulla (row×col) -->
    <div
      v-if="hoveredCell && cellMetricLines(hoveredCell.row, hoveredCell.col).length"
      class="cell-tooltip"
      :style="{ top: tooltipPos.y + 'px', left: tooltipPos.x + 'px' }"
    >
      <strong>{{ hoveredCell.row }} · {{ hoveredCell.col }}</strong>
      <div class="tooltip-sep"></div>
      <div v-for="(ml, mi) in cellMetricLines(hoveredCell.row, hoveredCell.col)" :key="mi" class="tooltip-metric">
        <span>{{ ml.name }}</span><b>{{ ml.value }}</b>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import * as XLSX from 'xlsx';

const props = defineProps({
  title: { type: String, default: '' },
  data: { type: Array, required: true },
  rowField: { type: String, required: true },
  colField: { type: String, required: true },
  valueField: { type: String, default: 'count' },
  rowLabel: { type: String, default: 'Categoria' },
  valueLabel: { type: String, default: 'Valor' },
  // Agregades opcionals: només a nivell de CEL·LA fulla (row×col = bucket real). Els
  // totals de fila/columna no porten agregada (AVG/MIN/MAX no es recombinen).
  metrics: { type: Array, default: () => [] },
  metricLabels: { type: Object, default: () => ({}) }
});

const METRIC_FUNC_LABELS = { SUM: 'Suma', AVG: 'Media', MIN: 'Mínimo', MAX: 'Máximo', MEDIAN: 'Mediana' };

// Ítem cru (amb claus FUNC(camp)) per cel·la row×col — la clau és la mateixa cadena
// que row.label/col perquè el tooltip la trobi.
const itemsByCell = computed(() => {
  const map = {};
  (props.data || []).forEach(item => {
    const rk = String(item[props.rowField] ?? 'N/A');
    const ck = String(item[props.colField] ?? 'N/A');
    (map[rk] || (map[rk] = {}))[ck] = item;
  });
  return map;
});

const hoveredCell = ref(null);
const tooltipPos = ref({ x: 0, y: 0 });

function onCellEnter(rowLabel, col, ev) {
  if (!props.metrics || !props.metrics.length) return;
  hoveredCell.value = { row: rowLabel, col };
  tooltipPos.value = { x: ev.clientX + 15, y: ev.clientY + 15 };
}
function onCellMove(ev) {
  if (hoveredCell.value) tooltipPos.value = { x: ev.clientX + 15, y: ev.clientY + 15 };
}
function onCellLeave() { hoveredCell.value = null; }

function cellMetricLines(rowLabel, col) {
  if (!props.metrics || !props.metrics.length) return [];
  const item = (itemsByCell.value[rowLabel] || {})[col];
  const lines = [];
  for (const m of props.metrics) {
    if (!m || !m.field || !Array.isArray(m.funcs)) continue;
    const leaf = String(m.field).split('.').pop();
    for (const func of m.funcs) {
      const expr = `${func}(${m.field})`;
      const cleanKey = expr.replace(/[\[\]" ]/g, '').toLowerCase();
      const name = props.metricLabels[cleanKey] || ((METRIC_FUNC_LABELS[func] || func) + ' ' + leaf);
      const v = item ? item[expr] : undefined;
      lines.push({ name, value: (v === undefined || v === null) ? '—' : Number(v).toLocaleString('es-ES', { maximumFractionDigits: 2 }) });
    }
  }
  return lines;
}

const columns = computed(() => {
  const set = new Set();
  (props.data || []).forEach(item => set.add(String(item[props.colField] ?? 'N/A')));
  return Array.from(set).sort();
});

const rows = computed(() => {
  const map = new Map();
  (props.data || []).forEach(item => {
    const rowKey = String(item[props.rowField] ?? 'N/A');
    const colKey = String(item[props.colField] ?? 'N/A');
    const value = Number(item[props.valueField]) || 0;
    if (!map.has(rowKey)) map.set(rowKey, { label: rowKey, cells: {}, total: 0 });
    const r = map.get(rowKey);
    r.cells[colKey] = (r.cells[colKey] || 0) + value;
    r.total += value;
  });
  const arr = Array.from(map.values());
  arr.sort((a, b) => {
    const aNum = Number(a.label);
    const bNum = Number(b.label);
    if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
    return String(a.label).localeCompare(String(b.label));
  });
  arr.forEach(r => {
    const num = Number(r.label);
    if (!isNaN(num) && r.label.includes('.')) r.label = Math.floor(num).toString();
  });
  return arr;
});

const colTotals = computed(() => {
  const totals = {};
  columns.value.forEach(c => totals[c] = 0);
  rows.value.forEach(r => {
    columns.value.forEach(c => {
      totals[c] += r.cells[c] || 0;
    });
  });
  return totals;
});

const grandTotal = computed(() => rows.value.reduce((sum, r) => sum + r.total, 0));

function formatNum(val) {
  return Number(val || 0).toLocaleString('es-ES');
}

function formatPct(val) {
  if (!val || isNaN(val)) return '0%';
  const v = val * 100;
  return v.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + '%';
}

function exportToExcel() {
  const rowsExport = rows.value.map(r => {
    const obj = { [props.rowLabel]: r.label };
    columns.value.forEach(c => { obj[c] = r.cells[c] || 0; });
    obj['Total'] = r.total;
    return obj;
  });
  const ws = XLSX.utils.json_to_sheet(rowsExport);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Matrix');
  const safe = (props.title || 'matrix').replace(/[^a-z0-9]/gi, '_').toLowerCase();
  XLSX.writeFile(wb, `${safe}.xlsx`);
}
</script>

<style scoped>
.matrix-widget {
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.widget-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}

.widget-header h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  margin: 0;
}

.export-btn {
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid #059669;
  background-color: #10b981;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.export-btn:hover { background-color: #059669; }

.table-scroll {
  flex: 1;
  overflow: auto;
  min-height: 0;
}

.matrix-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.matrix-table th {
  text-align: center;
  padding: 6px 8px;
  font-weight: 600;
  color: #374151;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 5;
}

.matrix-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
  text-align: right;
  white-space: nowrap;
}

.row-label {
  text-align: left;
  font-weight: 500;
  background: #fafafa;
}

.col-group {
  border-left: 1px solid #e5e7eb;
  text-transform: capitalize;
}

.val { text-align: right; }
.pct { text-align: right; color: #6b7280; }

tfoot .total-row {
  background: #eff6ff;
  border-top: 2px solid #0b5394;
}

tfoot .total-row td {
  color: #0b5394;
  padding: 10px 8px;
  position: sticky;
  bottom: 0;
}

.matrix-table tbody tr:hover td:not(.row-label) {
  background-color: #f9fafb;
}

.matrix-widget { position: relative; }

.metric-cell { cursor: help; }
.metric-cell:hover { background-color: #eef2ff !important; }

.cell-tooltip {
  position: fixed;
  background-color: rgba(17, 24, 39, 0.92);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.8rem;
  pointer-events: none;
  z-index: 1000;
  min-width: 160px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
}

.cell-tooltip .tooltip-sep {
  border-top: 1px solid rgba(255, 255, 255, 0.25);
  margin: 5px 0;
}

.cell-tooltip .tooltip-metric {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  line-height: 1.5;
}

.cell-tooltip .tooltip-metric span { color: #cbd5e1; }
</style>
